import { SmallLotPricingTier } from '@ravago/shared/page-data/models/elements';

export type QuantityChangeResult = { valid: true; quantity: number } | { valid: false; rawValue: number };

export interface QuantitySuggestion {
   quantity: number;
   tier: SmallLotPricingTier;
}

/** Sorts tiers ascending by their upper quantity bound. */
export const sortTiers = (tiers: SmallLotPricingTier[]): SmallLotPricingTier[] => [...tiers].sort((a, b) => a.quantityTo.amount - b.quantityTo.amount);

/** Upper bound of the highest tier (0 when there are no tiers). */
export const getLastTierQuantityTo = (sortedTiers: SmallLotPricingTier[]): number => sortedTiers.at(-1)?.quantityTo.amount ?? 0;

/**
 * Resolves the tier that applies to a quantity.
 * Quantities above the last tier's upper bound fall back to the last (best-price) tier.
 */
export const getTierForQuantity = (qty: number, sortedTiers: SmallLotPricingTier[], lastTierQuantityTo: number): SmallLotPricingTier | undefined => {
   const fallbackTier = sortedTiers.at(-1) ?? sortedTiers[0];
   if (!fallbackTier) return undefined;

   return qty > lastTierQuantityTo
      ? fallbackTier
      : (sortedTiers.find((t) => qty >= t.quantityFrom.amount && qty <= t.quantityTo.amount) ?? sortedTiers[0] ?? fallbackTier);
};

/** Index of the active tier for a quantity, or -1 when none matches. */
export const getActiveTierIndex = (qty: number, sortedTiers: SmallLotPricingTier[], lastTierQuantityTo: number): number =>
   sortedTiers.findIndex((tier, index) =>
      qty > lastTierQuantityTo ? index === sortedTiers.length - 1 : qty >= tier.quantityFrom.amount && qty <= tier.quantityTo.amount
   );

/** Parses raw input into a valid quantity (positive multiple of the packaging increment) or the invalid raw value. */
export const parseQuantity = (raw: string, packagingIncrement: number): QuantityChangeResult => {
   const parsed = Number.parseInt(raw, 10);
   return !Number.isNaN(parsed) && parsed > 0 && parsed % packagingIncrement === 0
      ? { valid: true, quantity: parsed }
      : { valid: false, rawValue: Number.isNaN(parsed) ? 0 : parsed };
};

export const isSameResult = (a: QuantityChangeResult, b: QuantityChangeResult): boolean => {
   if (a.valid && b.valid) return a.quantity === b.quantity;
   if (!a.valid && !b.valid) return a.rawValue === b.rawValue;
   return false;
};

/**
 * Builds the "closest valid quantity" nudge options for an invalid raw value.
 * Includes the nearest lower valid multiple (when one exists) and the nearest higher valid multiple,
 * sorted by lowest discounted price then lowest quantity.
 */
export const getQuantitySuggestions = (
   rawValue: number,
   increment: number,
   sortedTiers: SmallLotPricingTier[],
   lastTierQuantityTo: number
): QuantitySuggestion[] => {
   const qHigh = rawValue <= 0 ? increment : Math.ceil(rawValue / increment) * increment || increment;
   const qLow = rawValue <= 0 ? null : Math.floor(rawValue / increment) * increment;

   const options: QuantitySuggestion[] = [];
   if (qLow !== null && qLow >= increment) {
      const qLowTier = getTierForQuantity(qLow, sortedTiers, lastTierQuantityTo);
      if (qLowTier) options.push({ quantity: qLow, tier: qLowTier });
   }
   const qHighTier = getTierForQuantity(qHigh, sortedTiers, lastTierQuantityTo);
   if (qHighTier) options.push({ quantity: qHigh, tier: qHighTier });

   return options.sort((a, b) => {
      const priceDiff = a.tier.discountedPrice.amount - b.tier.discountedPrice.amount;
      return priceDiff !== 0 ? priceDiff : a.quantity - b.quantity;
   });
};
